import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarCheck2, Check, ChevronDown, ChevronUp, Download, LogOut, Mail, Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { useSite, DEFAULT_COLORS } from "../context/SiteContext";
import { FadeUp } from "../components/motion";
import { IMAGES } from "../data/products";

const SITE_IMAGE_DEFAULTS = {
  heroMain: IMAGES.heroMain,
  heroSecondary: IMAGES.heroSecondary,
  booking: IMAGES.booking,
  howHero: IMAGES.howHero,
  giftcard: IMAGES.giftcard,
};
const SITE_IMAGE_KEYS = ["heroMain", "heroSecondary", "booking", "howHero", "giftcard"];
const COLOR_PRESETS = [
  { name: "Rouge", accent: "#8A3A33", accentDark: "#702F29", blush: "#D9C5C1" },
  { name: "Olijf", accent: "#5A6238", accentDark: "#48502C", blush: "#D3D6B8" },
  { name: "Marine", accent: "#2E4057", accentDark: "#22334A", blush: "#C3CDDC" },
  { name: "Choco", accent: "#6B4226", accentDark: "#553420", blush: "#E3CDB8" },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = { withCredentials: true };

const inputCls =
  "w-full border border-ink/20 bg-cream px-4 py-3.5 text-sm text-ink focus:border-rouge focus:outline-none transition-colors duration-300";

const StatusBadge = ({ status, labels }) => {
  const styles = {
    pending: "bg-blush/50 text-ink",
    confirmed: "bg-rouge text-cream",
    cancelled: "bg-ink/10 text-stone2",
  };
  return (
    <span className={`inline-block px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${styles[status] || styles.pending}`} data-testid="booking-status-badge">
      {labels[status] || status}
    </span>
  );
};

const Admin = () => {
  const { t } = useLanguage();
  const a = t.admin;
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [cal, setCal] = useState({ connected: false, email: "" });
  const [schedule, setSchedule] = useState(null);
  const [newTime, setNewTime] = useState({});
  const [savingHours, setSavingHours] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [clientQuery, setClientQuery] = useState("");
  const [expandedClient, setExpandedClient] = useState(null);
  const { site, setSite, img } = useSite();
  const [siteDraft, setSiteDraft] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [savingSite, setSavingSite] = useState(false);

  useEffect(() => {
    if (site && !siteDraft) {
      setSiteDraft({ images: { ...(site.images || {}) }, colors: { ...DEFAULT_COLORS, ...(site.colors || {}) } });
    }
  }, [site, siteDraft]);

  const handleUpload = async (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, auth);
      setSiteDraft((s) => ({ ...s, images: { ...s.images, [key]: data.url } }));
    } catch {
      toast.error(a.loginError);
    } finally {
      setUploadingKey(null);
      e.target.value = "";
    }
  };

  const saveSite = async () => {
    setSavingSite(true);
    try {
      const { data } = await axios.put(`${API}/site`, siteDraft, auth);
      setSite(data);
      toast.success(a.siteSaved);
    } catch {
      toast.error(a.loginError);
    } finally {
      setSavingSite(false);
    }
  };

  const statusLabels = { pending: a.statusPending, confirmed: a.statusConfirmed, cancelled: a.statusCancelled };
  const serviceName = (id) => t.services.list.find((s) => s.id === id)?.name || id;

  const loadData = useCallback(async () => {
    try {
      const [b, m, g, s, n] = await Promise.all([
        axios.get(`${API}/bookings`, auth),
        axios.get(`${API}/messages`, auth),
        axios.get(`${API}/auth/google/status`, auth),
        axios.get(`${API}/schedule`, auth),
        axios.get(`${API}/newsletter`, auth),
      ]);
      setBookings(b.data);
      setMessages(m.data);
      setCal(g.data);
      setSchedule(s.data.days);
      setSubscribers(n.data);
    } catch {
      toast.error(a.loginError);
    }
  }, [a]);

  useEffect(() => {
    axios.get(`${API}/auth/me`, auth)
      .then((r) => setMe(r.data))
      .catch(() => setMe(false));
  }, []);

  useEffect(() => {
    if (me) loadData();
  }, [me, loadData]);

  useEffect(() => {
    if (params.get("calendar") === "connected") {
      toast.success(a.agendaConnectedToast);
      setParams({}, { replace: true });
    }
  }, [params, setParams, a]);

  const login = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, auth);
      setMe(data);
    } catch {
      toast.error(a.loginError);
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, auth).catch(() => {});
    setMe(false);
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/bookings/${id}`, { status }, auth);
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
    } catch {
      toast.error(a.loginError);
    }
  };

  const connectGoogle = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/google/login`, auth);
      window.location.href = data.authorization_url;
    } catch {
      toast.error("Google Calendar not configured");
    }
  };

  const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

  const addTime = (day) => {
    const val = (newTime[day] || "").trim();
    if (!/^\d{2}:\d{2}$/.test(val)) return;
    setSchedule((s) => ({ ...s, [day]: [...new Set([...(s[day] || []), val])].sort() }));
    setNewTime({ ...newTime, [day]: "" });
  };

  const removeTime = (day, time) => {
    setSchedule((s) => ({ ...s, [day]: s[day].filter((x) => x !== time) }));
  };

  const saveHours = async () => {
    setSavingHours(true);
    try {
      const { data } = await axios.put(`${API}/schedule`, { days: schedule }, auth);
      setSchedule(data.days);
      toast.success(a.hoursSaved);
    } catch {
      toast.error(a.loginError);
    } finally {
      setSavingHours(false);
    }
  };

  const clients = useMemo(() => {
    const map = {};
    const ensure = (email, name, phone) => {
      const key = (email || "").toLowerCase();
      if (!key) return null;
      if (!map[key]) map[key] = { email: key, name: name || key, phone: phone || "", bookings: [], messages: [], newsletter: false, lastActivity: "" };
      if (name) map[key].name = name;
      if (phone) map[key].phone = phone;
      return map[key];
    };
    bookings.forEach((b) => {
      const c = ensure(b.email, b.name, b.phone);
      if (c) { c.bookings.push(b); if (b.created_at > c.lastActivity) c.lastActivity = b.created_at; }
    });
    messages.forEach((m) => {
      const c = ensure(m.email, m.name);
      if (c) { c.messages.push(m); if (m.created_at > c.lastActivity) c.lastActivity = m.created_at; }
    });
    subscribers.forEach((s) => {
      const c = ensure(s.email);
      if (c) { c.newsletter = true; if (s.created_at > c.lastActivity) c.lastActivity = s.created_at; }
    });
    return Object.values(map).sort((x, y) => (y.lastActivity || "").localeCompare(x.lastActivity || ""));
  }, [bookings, messages, subscribers]);

  const filteredClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => c.name.toLowerCase().includes(q) || c.email.includes(q) || (c.phone || "").toLowerCase().includes(q));
  }, [clients, clientQuery]);

  const exportClients = () => {
    const header = ["Name", "Email", "Phone", "Appointments", "Messages", "Newsletter", "Last activity"];
    const rows = clients.map((c) => [
      c.name,
      c.email,
      c.phone,
      String(c.bookings.length),
      String(c.messages.length),
      c.newsletter ? "yes" : "no",
      c.lastActivity ? c.lastActivity.slice(0, 10) : "",
    ]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `onitnails-clients-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (me === null) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="admin-loading">
        <p className="overline-label text-stone2">On It Nails</p>
      </div>
    );
  }

  if (me === false) {
    return (
      <section className="mx-auto flex min-h-screen max-w-[1400px] items-center px-6 lg:px-12" data-testid="admin-login">
        <FadeUp className="w-full max-w-md border border-ink/15 bg-cream p-10">
          <p className="overline-label text-rouge">{a.title}</p>
          <h1 className="mt-4 font-display text-3xl font-bold">{a.loginTitle}</h1>
          <form onSubmit={login} className="mt-8 grid gap-5">
            <div>
              <label htmlFor="ad-email" className="overline-label mb-2 block text-stone2">{a.email}</label>
              <input id="ad-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-email-input" autoComplete="email" />
            </div>
            <div>
              <label htmlFor="ad-pass" className="overline-label mb-2 block text-stone2">{a.password}</label>
              <input id="ad-pass" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="admin-password-input" autoComplete="current-password" />
            </div>
            <button type="submit" disabled={loginLoading} data-testid="admin-login-button" className="bg-rouge px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:bg-rouge-dark disabled:opacity-60">
              {loginLoading ? a.loggingIn : a.loginCta}
            </button>
          </form>
        </FadeUp>
      </section>
    );
  }

  return (
    <section className="mx-auto min-h-screen max-w-[1400px] px-6 pb-28 pt-36 lg:px-12" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="overline-label text-rouge">{a.title}</p>
          <h1 className="mt-3 font-display text-4xl font-bold tracking-tight">On It Nails</h1>
          <p className="mt-1 text-sm text-stone2" data-testid="admin-email-label">{me.email}</p>
        </div>
        <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 border border-ink px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-ink hover:text-cream">
          <LogOut size={15} /> {a.logout}
        </button>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-6 border border-ink/15 bg-sand p-8" data-testid="google-calendar-card">
        <div className="flex items-start gap-4">
          <CalendarCheck2 size={22} className="mt-1 shrink-0 text-rouge" />
          <div>
            <p className="font-display text-xl font-bold">{a.calendarTitle}</p>
            <p className="mt-1 max-w-md text-sm leading-relaxed text-stone2">{a.calendarText}</p>
            <p className="mt-2 text-xs font-bold uppercase tracking-[0.15em]" data-testid="google-calendar-status">
              {cal.connected ? (
                <span className="text-rouge">{a.calendarConnected} {cal.email}</span>
              ) : (
                <span className="text-stone2">{a.calendarNotConnected}</span>
              )}
            </p>
          </div>
        </div>
        {!cal.connected && (
          <button onClick={connectGoogle} data-testid="google-connect-button" className="bg-ink px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-rouge">
            {a.calendarConnect}
          </button>
        )}
      </div>

      <div className="mt-12 flex gap-2 border-b border-ink/10" role="tablist">
        {[
          { id: "bookings", label: `${a.tabBookings} (${bookings.length})` },
          { id: "messages", label: `${a.tabMessages} (${messages.length})` },
          { id: "clients", label: `${a.tabClients} (${clients.length})` },
          { id: "hours", label: a.tabHours },
          { id: "site", label: a.tabSite },
        ].map((tb) => (
          <button
            key={tb.id}
            role="tab"
            aria-selected={tab === tb.id}
            onClick={() => setTab(tb.id)}
            data-testid={`admin-tab-${tb.id}`}
            className={`px-6 py-3.5 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${tab === tb.id ? "border-b-2 border-rouge text-rouge" : "text-stone2 hover:text-ink"}`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "site" ? (
        <div data-testid="site-settings">
          <p className="overline-label mt-6 text-stone2">{a.siteImagesTitle}</p>
          {siteDraft && (
            <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {SITE_IMAGE_KEYS.map((key) => (
                <div key={key} className="border border-ink/10 bg-sand p-4" data-testid={`site-image-${key}`}>
                  <div className="img-frame aspect-[4/3] bg-cream">
                    <img src={img(key, SITE_IMAGE_DEFAULTS[key])} alt={a.imageLabels[key]} className="h-full w-full object-cover" />
                  </div>
                  <p className="mt-3 text-sm font-bold">{a.imageLabels[key]}</p>
                  {siteDraft.images[key] && (
                    <button onClick={() => setSiteDraft((s) => { const images = { ...s.images }; delete images[key]; return { ...s, images }; })} data-testid={`site-image-reset-${key}`} className="mt-1 text-xs text-stone2 link-underline">
                      {a.resetImage}
                    </button>
                  )}
                  <label className="mt-3 inline-flex cursor-pointer items-center gap-2 border border-ink px-4 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors duration-300 hover:bg-ink hover:text-cream">
                    <Upload size={13} /> {uploadingKey === key ? a.uploading : a.uploadCta}
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => handleUpload(key, e)} data-testid={`site-upload-${key}`} />
                  </label>
                </div>
              ))}
            </div>
          )}
          <p className="overline-label mt-12 text-stone2">{a.siteColorsTitle}</p>
          <div className="mt-4 flex flex-wrap gap-3" data-testid="color-presets">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset.name}
                onClick={() => setSiteDraft((s) => ({ ...s, colors: { accent: preset.accent, accentDark: preset.accentDark, blush: preset.blush } }))}
                data-testid={`preset-${preset.name.toLowerCase()}`}
                className="flex items-center gap-2 border border-ink/15 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-300 hover:border-ink"
              >
                <span className="flex -space-x-1.5">
                  <span className="h-4 w-4 rounded-full border border-cream" style={{ background: preset.accent }} />
                  <span className="h-4 w-4 rounded-full border border-cream" style={{ background: preset.accentDark }} />
                  <span className="h-4 w-4 rounded-full border border-cream" style={{ background: preset.blush }} />
                </span>
                {preset.name}
              </button>
            ))}
          </div>
          {siteDraft && (
            <div className="mt-6 flex flex-wrap gap-8">
              {[["accent", a.accentLabel], ["accentDark", a.accentDarkLabel], ["blush", a.blushLabel]].map(([k, label]) => (
                <label key={k} className="flex items-center gap-3 text-sm font-semibold" data-testid={`color-field-${k}`}>
                  <input
                    type="color"
                    value={siteDraft.colors[k] || "#8A3A33"}
                    onChange={(e) => setSiteDraft((s) => ({ ...s, colors: { ...s.colors, [k]: e.target.value } }))}
                    className="h-10 w-14 cursor-pointer border border-ink/20 bg-cream"
                    data-testid={`color-input-${k}`}
                  />
                  {label}
                  <span className="text-xs text-stone2">{siteDraft.colors[k]}</span>
                </label>
              ))}
            </div>
          )}
          <div className="mt-10">
            <button onClick={saveSite} disabled={savingSite || !siteDraft} data-testid="site-save-button" className="bg-rouge px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-rouge-dark disabled:opacity-60">
              {savingSite ? a.loggingIn : a.saveHours}
            </button>
          </div>
        </div>
      ) : tab === "clients" ? (
        <div data-testid="clients-section">
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <input
              value={clientQuery}
              onChange={(e) => setClientQuery(e.target.value)}
              placeholder={a.clientSearch}
              aria-label={a.clientSearch}
              className="w-full max-w-md border border-ink/20 bg-cream px-4 py-3 text-sm transition-colors duration-300 focus:border-rouge focus:outline-none"
              data-testid="client-search-input"
            />
            <button
              onClick={exportClients}
              disabled={clients.length === 0}
              data-testid="clients-export-button"
              className="inline-flex items-center gap-2 border border-ink px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-ink hover:text-cream disabled:opacity-50"
            >
              <Download size={14} /> {a.exportClients}
            </button>
          </div>
          {filteredClients.length === 0 ? (
            <p className="py-16 text-sm text-stone2" data-testid="no-clients">{clients.length === 0 ? a.noClients : a.noResults}</p>
          ) : (
            <div className="mt-6 divide-y divide-ink/10 border-t border-ink/10" data-testid="clients-list">
              {filteredClients.map((c, i) => (
                <div key={c.email} data-testid={`client-row-${i}`}>
                  <button
                    onClick={() => setExpandedClient(expandedClient === c.email ? null : c.email)}
                    aria-expanded={expandedClient === c.email}
                    data-testid={`client-expand-${i}`}
                    className="flex w-full flex-wrap items-center gap-x-6 gap-y-2 py-5 text-left"
                  >
                    <p className="w-44 font-display text-lg font-bold">{c.name}</p>
                    <p className="text-sm text-rouge">{c.email}</p>
                    {c.phone && <p className="text-sm text-stone2">{c.phone}</p>}
                    <span className="ml-auto flex items-center gap-3 text-xs text-stone2">
                      {c.newsletter && (
                        <span className="border border-blush bg-blush/30 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-ink">{a.newsletterBadge}</span>
                      )}
                      {c.bookings.length} {a.appointments} · {c.messages.length} {a.messagesLabel}
                      {expandedClient === c.email ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {expandedClient === c.email && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="grid gap-8 pb-8 pl-1 md:grid-cols-2" data-testid={`client-history-${i}`}>
                          <div>
                            <p className="overline-label text-stone2">{a.tabBookings}</p>
                            {c.bookings.length === 0 && <p className="mt-3 text-sm text-stone2">—</p>}
                            {c.bookings.map((b) => (
                              <div key={b.id} className="mt-3 flex items-center gap-4 border border-ink/10 bg-sand px-4 py-3">
                                <div>
                                  <p className="text-sm font-bold">{b.date} · {b.time}</p>
                                  <p className="text-xs text-stone2">{serviceName(b.service)}</p>
                                </div>
                                <span className="ml-auto"><StatusBadge status={b.status} labels={statusLabels} /></span>
                              </div>
                            ))}
                          </div>
                          <div>
                            <p className="overline-label text-stone2">{a.tabMessages}</p>
                            {c.messages.length === 0 && <p className="mt-3 text-sm text-stone2">—</p>}
                            {c.messages.map((m) => (
                              <div key={m.id} className="mt-3 border border-ink/10 bg-sand px-4 py-3">
                                <p className="text-xs text-stone2">{new Date(m.created_at).toLocaleDateString()}</p>
                                <p className="mt-1 text-sm leading-relaxed text-ink">{m.message}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : tab === "hours" ? (
        <div data-testid="hours-editor">
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-stone2">{a.hoursIntro}</p>
          {schedule && (
            <>
              <div className="mt-6">
                {DAY_ORDER.map((day) => (
                  <div key={day} className="flex flex-wrap items-center gap-4 border-b border-ink/10 py-5" data-testid={`hours-row-${day}`}>
                    <p className="w-32 font-display text-lg font-bold">{a.days[day]}</p>
                    <div className="flex flex-1 flex-wrap items-center gap-2">
                      {(schedule[day] || []).length === 0 && (
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone2">{a.closedLabel}</span>
                      )}
                      {(schedule[day] || []).map((time) => (
                        <span key={time} className="inline-flex items-center gap-1.5 border border-ink/20 px-3 py-1.5 text-sm font-semibold" data-testid={`hours-chip-${day}-${time.replace(":", "")}`}>
                          {time}
                          <button onClick={() => removeTime(day, time)} aria-label={`Remove ${time}`} data-testid={`hours-remove-${day}-${time.replace(":", "")}`} className="text-stone2 transition-colors duration-300 hover:text-rouge">
                            <X size={13} />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        value={newTime[day] || ""}
                        onChange={(e) => setNewTime({ ...newTime, [day]: e.target.value })}
                        placeholder="08:30"
                        aria-label={`New time ${day}`}
                        className="w-24 border border-ink/20 bg-cream px-3 py-2 text-sm transition-colors duration-300 focus:border-rouge focus:outline-none"
                        data-testid={`hours-input-${day}`}
                      />
                      <button onClick={() => addTime(day)} aria-label={`Add time ${day}`} data-testid={`hours-add-${day}`} className="border border-ink p-2 transition-colors duration-300 hover:bg-ink hover:text-cream">
                        <Plus size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={saveHours} disabled={savingHours} data-testid="hours-save-button" className="mt-8 bg-rouge px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-rouge-dark disabled:opacity-60">
                {savingHours ? a.loggingIn : a.saveHours}
              </button>
            </>
          )}
        </div>
      ) : tab === "bookings" ? (
        bookings.length === 0 ? (
          <p className="py-16 text-sm text-stone2" data-testid="no-bookings">{a.noBookings}</p>
        ) : (
          <div className="divide-y divide-ink/10" data-testid="bookings-list">
            {bookings.map((b) => (
              <div key={b.id} className={`flex flex-wrap items-center gap-x-8 gap-y-4 py-6 ${b.status === "cancelled" ? "opacity-50" : ""}`} data-testid={`booking-row-${b.id.slice(0, 8)}`}>
                <div className="w-32">
                  <p className="font-display text-lg font-bold">{b.date}</p>
                  <p className="text-sm font-semibold text-rouge">{b.time}</p>
                </div>
                <div className="min-w-40 flex-1">
                  <p className="text-sm font-bold">{serviceName(b.service)}</p>
                  <p className="text-sm text-stone2">{b.name} · {b.phone}</p>
                  <p className="text-xs text-stone2">{b.email}</p>
                  {b.notes && <p className="mt-1 text-xs italic text-stone2">"{b.notes}"</p>}
                </div>
                <StatusBadge status={b.status} labels={statusLabels} />
                <span
                  className={`px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${b.payment_status === "paid" ? "bg-ink text-cream" : "border border-ink/20 text-stone2"}`}
                  data-testid={`booking-payment-${b.id.slice(0, 8)}`}
                >
                  {b.payment_status === "paid" ? a.depositPaid : a.depositOpen}
                </span>
                <div className="flex gap-2">
                  {b.status === "pending" && (
                    <button onClick={() => setStatus(b.id, "confirmed")} data-testid={`booking-confirm-${b.id.slice(0, 8)}`} className="inline-flex items-center gap-1.5 border border-rouge px-4 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-rouge transition-colors duration-300 hover:bg-rouge hover:text-cream">
                      <Check size={13} /> {a.confirm}
                    </button>
                  )}
                  {b.status !== "cancelled" && (
                    <button onClick={() => setStatus(b.id, "cancelled")} data-testid={`booking-cancel-${b.id.slice(0, 8)}`} className="inline-flex items-center gap-1.5 border border-ink/25 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-stone2 transition-colors duration-300 hover:border-ink hover:text-ink">
                      <X size={13} /> {a.cancelBooking}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : messages.length === 0 ? (
        <p className="py-16 text-sm text-stone2" data-testid="no-messages">{a.noMessages}</p>
      ) : (
        <div className="divide-y divide-ink/10" data-testid="messages-list">
          {messages.map((m) => (
            <div key={m.id} className="py-6" data-testid={`message-row-${m.id.slice(0, 8)}`}>
              <div className="flex flex-wrap items-center gap-3">
                <Mail size={15} className="text-rouge" />
                <p className="text-sm font-bold">{m.name}</p>
                <a href={`mailto:${m.email}`} className="link-underline text-xs text-rouge">{m.email}</a>
                <span className="text-xs text-stone2">{new Date(m.created_at).toLocaleDateString()}</span>
              </div>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-stone2">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default Admin;
